<script setup>
import { computed, onMounted, ref } from 'vue';
import moment from 'moment';
import HeaderTitle from '@/components/layout/HeaderTitle';
import SharedElementPagination from '@/components/shared/SharedElementPagination.vue';
import MyAbsenceEditForm from './MyAbsenceEditForm.vue';
import AbsenceService from '@/api/services/absence.service';
import { useAuthStore } from '@/app/stores/authStore';
import { useUiStore } from '@/app/stores/uiStore';
import { useTableSort } from '@/app/composables/useTableSort';
import notification from '@/utils/plugins/NotificationService';

const authStore = useAuthStore();
const uiStore = useUiStore();
const absences = ref([]);
const page = ref(1);
const itemsPerPage = ref(10);
const totalResults = ref(0);
const fromDate = ref(null);
const toDate = ref(null);
const appliedDates = ref({});
const errorMessage = ref('');
const selectedAbsence = ref(null);
const showDetail = ref(false);
const savingDetail = ref(false);
const absenceForm = ref(null);
const employeeId = computed(() => authStore.getUserLogged?.id);
const { applySortParams, toggleSort, getSortIcon } = useTableSort();
let requestId = 0;

const formatDate = value => value ? moment(value).format('DD/MM/YYYY HH:mm') : '—';
const formatCardDate = value => value ? moment(value).format('DD/MM/YYYY') : '—';
const formatCardTime = value => value ? moment(value).format('HH:mm') : '';
const formatDuration = value => {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const minutes = Math.max(0, Number(value));
  return Math.floor(minutes / 60) + ' h ' + String(minutes % 60).padStart(2, '0') + ' min';
};
const disableFromDate = date => !!toDate.value && moment(date).isAfter(toDate.value, 'day');
const disableToDate = date => !!fromDate.value && moment(date).isBefore(fromDate.value, 'day');

async function getAbsences() {
  const currentRequest = ++requestId;
  errorMessage.value = '';
  absences.value = [];
  totalResults.value = 0;
  // Never send an unscoped request, including when the session is unavailable.
  if (!employeeId.value) {
    errorMessage.value = 'No se ha podido identificar al empleado conectado.';
    return;
  }
  const params = applySortParams({
    page: page.value,
    itemsPerPage: itemsPerPage.value,
    'employee.id': employeeId.value,
    ...appliedDates.value,
  });
  uiStore.startLoading();
  try {
    const response = await AbsenceService.getAbsences(params);
    if (currentRequest !== requestId) return;
    const data = response?.data;
    if (!Array.isArray(data?.['hydra:member']) || !Number.isFinite(data?.['hydra:totalItems'])) {
      throw new Error('Respuesta de ausencias inválida');
    }
    // The API must enforce ownership too. Reject unexpected employee data.
    const ownAbsences = data['hydra:member'];
    if (ownAbsences.some(item => {
      const owner = item.employee;
      const id = typeof owner === 'string' ? owner.split('/').pop()
        : owner?.id ?? owner?.['@id']?.split('/').pop();
      return String(id) !== String(employeeId.value);
    })) {
      throw new Error('La respuesta contiene ausencias de otro empleado');
    }
    absences.value = ownAbsences;
    totalResults.value = data['hydra:totalItems'];
  } catch {
    if (currentRequest !== requestId) return;
    errorMessage.value = 'No se han podido cargar tus ausencias. Vuelve a intentarlo.';
    notification.addNotification({ type: 'error', message: errorMessage.value });
  } finally {
    uiStore.stopLoading();
  }
}

function search() {
  if (fromDate.value && toDate.value && fromDate.value > toDate.value) {
    notification.addNotification({ type: 'error', message: 'La fecha Desde no puede ser posterior a Hasta.' });
    return;
  }
  appliedDates.value = {};
  if (fromDate.value) appliedDates.value['fromDate[after]'] = fromDate.value + ' 00:00:00';
  if (toDate.value) appliedDates.value['toDate[before]'] = toDate.value + ' 23:59:59';
  page.value = 1;
  return getAbsences();
}

function sortByDuration() {
  toggleSort('absentWorkTime');
  page.value = 1;
  return getAbsences();
}

function openDetail(absence) {
  savingDetail.value = false;
  selectedAbsence.value = absence;
  showDetail.value = true;
}

function closeDetail() {
  if (savingDetail.value) return;
  if (absenceForm.value) absenceForm.value.requestClose();
  else finishCloseDetail();
}
function finishCloseDetail() {
  if (savingDetail.value) return;
  showDetail.value = false;
}
async function savedAbsence() {
  savingDetail.value = false;
  showDetail.value = false;
  await getAbsences();
}
onMounted(getAbsences);
</script>

<template>
  <div class="my-absences">
    <HeaderTitle title="Talento" />
    <div class="d-flex align-items-center gap-3">
      <img src="@/assets/icons/search-blue.svg" alt="">
      <h1 class="title-text">Mis ausencias</h1>
    </div>
    <el-card class="card-search w-100 mt-3">
      <form class="row g-3 align-items-end" @submit.prevent="search">
        <div class="col-12 col-md-4">
          <div class="form-item date-picker">
            <label for="my-absences-from" class="w-100 mb-2">Desde</label>
            <el-date-picker id="my-absences-from" v-model="fromDate" type="date" class="w-100"
              format="DD/MM/YYYY" value-format="YYYY-MM-DD" placeholder="Fecha de inicio"
              :disabled-date="disableFromDate" clearable />
          </div>
        </div>
        <div class="col-12 col-md-4">
          <div class="form-item date-picker">
            <label for="my-absences-to" class="w-100 mb-2">Hasta</label>
            <el-date-picker id="my-absences-to" v-model="toDate" type="date" class="w-100"
              format="DD/MM/YYYY" value-format="YYYY-MM-DD" placeholder="Fecha de fin"
              :disabled-date="disableToDate" clearable />
          </div>
        </div>
        <div class="col-12 col-md-4 absence-search-actions">
          <button type="submit" class="btn btn_red btn-search absence-search" :disabled="uiStore.loading">BUSCAR</button>
        </div>
      </form>
    </el-card>

    <p v-if="errorMessage" class="mt-4 text-danger" role="alert">{{ errorMessage }}</p>
    <p v-else-if="!absences.length && !uiStore.loading" class="mt-4">No se han encontrado ausencias con los filtros seleccionados.</p>
    <template v-if="absences.length">
      <div class="table table-not-mobile">
        <table>
          <thead>
            <tr>
              <th>Inicio</th>
              <th>Fin</th>
              <th>
                <button type="button" class="sort-button" @click="sortByDuration">
                  Duración <font-awesome-icon :icon="getSortIcon('absentWorkTime')" />
                </button>
              </th>
              <th>Motivo</th>
              <th>Justificantes</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="absence in absences" :key="absence.id" class="pointer" tabindex="0"
              aria-label="Ver detalle de la ausencia" @click="openDetail(absence)"
              @keydown.enter="openDetail(absence)" @keydown.space.prevent="openDetail(absence)">
              <td>{{ formatDate(absence.fromDate) }}</td>
              <td>{{ formatDate(absence.toDate) }}</td>
              <td>{{ formatDuration(absence.absentWorkTime) }}</td>
              <td>{{ absence.absenceType?.name || '—' }}</td>
              <td>{{ absence.absenceSupportingDocuments?.length || 0 }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="table-mobile">
        <el-card v-for="absence in absences" :key="absence.id" class="absence-card mt-3 pointer"
          tabindex="0" role="button" aria-label="Ver detalle de la ausencia"
          @click="openDetail(absence)" @keydown.enter="openDetail(absence)"
          @keydown.space.prevent="openDetail(absence)">
          <div class="absence-card__grid">
            <p v-if="absence.timestamp"><strong>Fecha: </strong>{{ formatCardDate(absence.timestamp) }}</p>
            <p :class="{ 'absence-card__wide': !absence.timestamp }"><strong>Duración: </strong>{{ formatDuration(absence.absentWorkTime) }}</p>
            <p>
              <strong>Inicio: </strong>{{ formatCardDate(absence.fromDate) }}
              <span v-if="absence.fromDate" class="absence-card__time">{{ formatCardTime(absence.fromDate) }}</span>
            </p>
            <p>
              <strong>Fin: </strong>{{ formatCardDate(absence.toDate) }}
              <span v-if="absence.toDate" class="absence-card__time">{{ formatCardTime(absence.toDate) }}</span>
            </p>
            <p class="absence-card__wide"><strong>Justificantes: </strong>{{ absence.absenceSupportingDocuments?.length || 0 }}</p>
            <p class="absence-card__wide"><strong>Motivo: </strong>{{ absence.absenceType?.name || '—' }}</p>
            <p class="absence-card__wide absence-card__notes"><strong>Observaciones: </strong>{{ absence.observations || 'Sin observaciones' }}</p>
          </div>
        </el-card>
      </div>
    </template>
    <SharedElementPagination v-model:current-page="page" v-model:page-size="itemsPerPage"
      :total="totalResults" :disabled="uiStore.loading" class="tracker-pagination" @change="getAbsences" />

    <el-dialog v-model="showDetail" title="Detalle de mi ausencia" class="my-absence-dialog"
      width="700px" :lock-scroll="true" :show-close="false" :close-on-click-modal="!savingDetail"
      :close-on-press-escape="!savingDetail" :before-close="closeDetail" @closed="selectedAbsence = null">
      <template #header="{ titleId }">
        <div class="absence-dialog-header">
          <img src="/calendar-blue.svg" alt="">
          <h2 :id="titleId">Detalle de mi ausencia</h2>
          <button type="button" class="absence-dialog-close" aria-label="Cerrar detalle de mi ausencia"
            :disabled="savingDetail" @click="closeDetail">
            <img src="@/assets/images/aspa_blue_small_entrada.png" alt="">
          </button>
        </div>
      </template>
      <MyAbsenceEditForm ref="absenceForm" v-if="selectedAbsence"
        :absence="selectedAbsence" :read-only="selectedAbsence.createdFromPresence !== true"
        @busy="savingDetail = $event" @saved="savedAbsence" @cancel="finishCloseDetail" />
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.my-absences {
  min-width: 0;
  .title-text { margin: 0; }
  .card-search { height: auto; }
  .form-item { margin-bottom: 0; }
  .absence-search-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
  .absence-search {
    height: 44px;
    min-height: 44px;
    width: 180px;
    max-width: 100%;
    margin: 0 !important;
    padding: 0 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff !important;
    &:hover { color: #fff !important; }
  }
  button:disabled { cursor: not-allowed; opacity: 0.5; }
  .sort-button { border: 0; background: transparent; color: inherit; font: inherit; padding: 0; }
  td { overflow-wrap: anywhere; }
  .absence-card { width: 100%; overflow-wrap: anywhere; }
  .absence-card:focus-visible, tr:focus-visible { outline: 2px solid #409eff; outline-offset: 2px; }
  :deep(.el-date-editor.el-input) {
    width: 100%;
    min-width: 0;
    height: 44px;
    align-items: center;
  }
}
@media (min-width: 768px) { .table-mobile { display: none; } }
@media (max-width: 767px) {
  .table-not-mobile { display: none; }
  .my-absences .absence-card {
    min-width: 0;
    border-radius: 16px;
    border: 1px solid #ebeef5;
    box-shadow: 0 3px 12px rgba(38, 48, 82, .08);
    font-size: 15px;
    color: #263052;
    :deep(.el-card__body) { padding: 16px 20px; }
    &__grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(min(100%, 145px), 1fr));
      gap: 4px 12px;
      min-width: 0;
    }
    p { min-width: 0; margin: 0; line-height: 1.5; overflow-wrap: anywhere; }
    strong { color: #474ce6; font-size: 16px; font-weight: 800; letter-spacing: -.05em; }
    &__wide { grid-column: 1 / -1; }
    &__time { display: inline-block; white-space: nowrap; }
    &__notes { white-space: pre-wrap; }
  }
}
</style>

<style lang="scss">
.el-dialog.my-absence-dialog {
  max-width: calc(100vw - 24px);
  border-radius: 20px;
  padding: 24px;
  .el-dialog__header { padding: 0 0 24px; margin: 0; }
  .el-dialog__body { padding: 0; }
  .el-dialog__footer { padding: 24px 0 0; }
  .absence-dialog-header {
    display: flex;
    align-items: center;
    gap: 12px;
    img { width: 30px; height: 30px; flex-shrink: 0; }
    h2 { flex: 1; min-width: 0; margin: 0; color: #474ce6; font-size: 30px; font-weight: 800; letter-spacing: -0.05em; overflow-wrap: anywhere; }
  }
  .absence-dialog-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    margin: 0;
    padding: 10px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
    img { width: 24px; height: 24px; object-fit: contain; }
    &:hover { background: #f1f2ff; }
    &:focus-visible { outline: 2px solid #409eff; outline-offset: 2px; }
    &:disabled { opacity: .5; cursor: not-allowed; }
  }
  .absence-value {
    min-height: 44px;
    padding: 11px 16px;
    border: 1px solid #dcdfe6;
    border-radius: 20px;
    background: #f5f7fa;
    color: #434343;
    font-size: 15px;
    line-height: 22px;
    overflow-wrap: anywhere;
  }
  .absence-notes { min-height: 112px; white-space: pre-wrap; }
  .absence-empty { color: #73767a; }
  .absence-dialog-actions, .edit-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    .btn { min-width: 140px; min-height: 44px; margin: 0; padding: 10px 24px; }
  }
  @media (max-width: 767px) {
    padding: 20px 16px;
    .absence-dialog-header { gap: 10px; h2 { font-size: 24px; } }
    .absence-dialog-actions, .edit-actions { flex-direction: column; .btn { width: 100%; min-width: 0; } }
    .document-link { align-items: flex-start; flex-direction: column; gap: 8px; }
  }
}
</style>
